import { useEffect, useRef, useState } from 'react';
import { useSsoReauth } from '../contexts/SsoReauthContext';
import type {
  MultiPackagePrintRequest,
  MultiPackagePrintResult,
  MultiPackagePrintResultItem,
  PrintPackageMode,
  SelectedChildPackage,
} from '../types/multiPackagePrint';
import downloadPrintRunOutput from '../utils/downloadPrintRunOutput';
import { getErrorBody, getErrorMessage } from '../utils/requestErrors';
import { createPrintRunCsvArtifact } from '../utils/exportPrintRunJob';
import { isMultiPackageLabelGenerationEnabled } from '../features/multiPackagePrint/capabilities';
import {
  confirmMultiPackageOutput,
  generateMultiPackagePrint,
  importChildPackage,
  retryMultiPackagePrint,
  useMultiPackagePrintCapabilities,
  useChildPackageCandidates,
} from '../features/multiPackagePrint/api';
import { createMultiPackageZip, downloadArtifact } from '../features/multiPackagePrint/multiPackageZip';
import { commonCasePlan, effectiveMultiPackageLimits, isValidLabelCount, reconcileSelectedChildPackages, selectionLimitErrors } from '../features/multiPackagePrint/childPackageSelection';

export type MultiPackageSourceIdentity = {
  organizationId: number;
  sourcePackageId: number;
  sourceMetrcPackageId: number;
  facilityId: number;
  packageLabel: string;
  quantity?: number | null;
};

export type MultiPackageSharedSettings = {
  templateId: number;
  jobName?: string | null;
  jobId?: string | null;
  packagedDate?: string | null;
  packagedDateExplicitlyBlank?: boolean;
  labelTemplateData?: string | null;
};

type Props = {
  enabled?: boolean;
  source: MultiPackageSourceIdentity | null;
  settings: MultiPackageSharedSettings | null;
  mode: PrintPackageMode;
};

function createOperationKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function resultPackageLabel(item: MultiPackagePrintResultItem): string {
  return item.packageLabel || `METRC package ${item.metrcPackageId}`;
}

export function buildMultiPackagePrintRequest(
  source: MultiPackageSourceIdentity,
  settings: MultiPackageSharedSettings,
  selectedChildren: SelectedChildPackage[],
  idempotencyKey: string,
): MultiPackagePrintRequest {
  return {
    organizationId: source.organizationId,
    sourcePackageId: source.sourcePackageId,
    sourceMetrcPackageId: source.sourceMetrcPackageId,
    facilityId: source.facilityId,
    ...settings,
    idempotencyKey,
    items: selectedChildren.map((child, sequence) => ({
      sequence,
      metrcPackageId: child.metrcPackageId,
      count: child.count!,
      productOverrideId: child.productOverrideId ?? null,
    })),
  };
}

export default function useMultiPackagePrintWorkflow({
  enabled,
  source,
  settings,
  mode,
}: Props) {
  const configuredActive = isMultiPackageLabelGenerationEnabled(enabled);
  const { handleSsoError } = useSsoReauth();
  const capability = useMultiPackagePrintCapabilities(configuredActive);
  const active = configuredActive && capability.ready;
  const limits = effectiveMultiPackageLimits(capability.data?.safetyLimits ? {
    children: capability.data.safetyLimits.maxSelectedChildren,
    labels: capability.data.safetyLimits.maxRequestedLabels,
    estimatedZipBytes: capability.data.safetyLimits.maxEstimatedZipBytes,
  } : undefined);
  const [selectedChildren, setSelectedChildren] = useState<SelectedChildPackage[]>([]);
  const [result, setResult] = useState<MultiPackagePrintResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importProductIds, setImportProductIds] = useState<Record<number, number | undefined>>({});
  const [importingIds, setImportingIds] = useState<number[]>([]);
  const importingIdsRef = useRef(new Set<string>());
  const [importErrors, setImportErrors] = useState<Record<number, string | undefined>>({});
  const operationKeyRef = useRef<string | null>(null);
  const generationInFlightRef = useRef<number | null>(null);
  const generationLeaseRef = useRef<symbol | null>(null);
  const zipInFlightRef = useRef<number | null>(null);
  const requestGenerationRef = useRef(0);
  const selectedChildrenRef = useRef(selectedChildren);
  selectedChildrenRef.current = selectedChildren;
  const sourceIdentity = source
    ? `${source.organizationId}:${source.sourcePackageId}:${source.sourceMetrcPackageId}:${source.facilityId}`
    : '';
  const sourceScopeRef = useRef({ identity: sourceIdentity, version: 0 });
  // A new visit to the same source is still a different request scope. Comparing
  // identifiers alone would let an old import commit after switching away/back.
  if (sourceScopeRef.current.identity !== sourceIdentity) {
    sourceScopeRef.current = { identity: sourceIdentity, version: sourceScopeRef.current.version + 1 };
  }
  const settingsIdentity = settings ? JSON.stringify(settings) : '';
  const contextIdentity = `${sourceScopeRef.current.version}:${mode}:${settingsIdentity}:${active}`;
  const contextRef = useRef(contextIdentity);
  if (contextRef.current !== contextIdentity) {
    contextRef.current = contextIdentity;
    requestGenerationRef.current += 1;
    operationKeyRef.current = null;
  }
  const {
    data: candidates,
    error: discoveryError,
    isLoading: isDiscovering,
    refresh: refreshCandidates,
    updateCandidate,
  } = useChildPackageCandidates(
    source ? {
      organizationId: source.organizationId,
      sourcePackageId: source.sourcePackageId,
      facilityId: source.facilityId,
    } : null,
    active && mode === 'children',
  );

  // SSO organizations only: offer the Metrc sign-in when the child list could not be loaded for lack of one.
  // Prompts once per failed load, not when the handler identity changes.
  useEffect(() => {
    if (discoveryError) handleSsoError(discoveryError);
  }, [discoveryError]);

  useEffect(() => {
    requestGenerationRef.current += 1;
    setResult(null);
    setErrorMessage(null);
    operationKeyRef.current = null;
    setIsGenerating(false);
    setIsDownloadingZip(false);
  }, [contextIdentity]);

  useEffect(() => () => {
    requestGenerationRef.current += 1;
    sourceScopeRef.current = { ...sourceScopeRef.current, version: sourceScopeRef.current.version + 1 };
  }, []);

  useEffect(() => {
    setSelectedChildren([]);
    setImportProductIds({});
    setImportingIds([]);
    setImportErrors({});
  }, [source?.organizationId, source?.facilityId, source?.sourceMetrcPackageId, source?.sourcePackageId]);

  useEffect(() => {
    const current = selectedChildrenRef.current;
    const reconciled = reconcileSelectedChildPackages(candidates, current);
    if (reconciled.length !== current.length || reconciled.some((child, index) =>
      child.metrcPackageId !== current[index].metrcPackageId
      || child.packageId !== current[index].packageId
      || child.productId !== current[index].productId
      || child.quantity !== current[index].quantity)) {
      requestGenerationRef.current += 1;
      operationKeyRef.current = null;
      setResult(null);
      setErrorMessage(null);
      setIsGenerating(false);
      setIsDownloadingZip(false);
    }
    setSelectedChildren(reconciled);
  }, [candidates]);

  const childModeUnavailableReason = !active
    ? 'Multi-package contracts are unavailable or incompatible. Child-package generation is disabled.'
    : !source
      ? 'Select a source package with a Retail ID package identity and facility before using child-package mode.'
      : !settings
        ? 'Select a label template and wait for print settings to load before using child-package mode.'
        : null;

  const run = async (resume = false) => {
    if (!active || mode !== 'children' || !source || !settings) return;
    if (resume && !result) return;
    if (!resume && (selectedChildren.length === 0
      || selectedChildren.some((child) => !isValidLabelCount(child.count))
      || selectedChildren.some((child) => !child.productId && !child.productOverrideId)
      || selectionLimitErrors(selectedChildren, undefined, limits).length > 0)) return;
    const requestGeneration = requestGenerationRef.current;
    if (generationInFlightRef.current === requestGeneration || zipInFlightRef.current === requestGeneration) return;
    const generationLease = Symbol('multi-package-generation');
    generationLeaseRef.current = generationLease;
    generationInFlightRef.current = requestGeneration;
    setIsGenerating(true);
    setErrorMessage(null);
    try {
      if (resume && result) {
        const nextResult = await retryMultiPackagePrint(source.organizationId, result.batchId);
        if (requestGeneration === requestGenerationRef.current) setResult(nextResult);
      } else {
        operationKeyRef.current ??= createOperationKey();
        const nextResult = await generateMultiPackagePrint(buildMultiPackagePrintRequest(
          source, settings, selectedChildren, operationKeyRef.current,
        ));
        if (requestGeneration === requestGenerationRef.current) setResult(nextResult);
      }
    } catch (error) {
      if (requestGeneration === requestGenerationRef.current) {
        // SSO organizations only: the preflight found no working Metrc sign-in. The inline message stays as well.
        handleSsoError(error);
        const body = getErrorBody(error) as { children?: Array<{ metrcPackageId: number; message: string }> } | undefined;
        setErrorMessage(body?.children?.length
          ? body.children.map((child) => `${candidates.find((candidate) => candidate.metrcPackageId === child.metrcPackageId)?.packageLabel ?? child.metrcPackageId}: ${child.message}`).join(' ')
          : getErrorMessage(error, 'Multi-package generation failed.'));
      }
    } finally {
      if (generationLeaseRef.current === generationLease) {
        generationLeaseRef.current = null;
        generationInFlightRef.current = null;
        if (requestGeneration === requestGenerationRef.current) setIsGenerating(false);
      }
    }
  };

  const downloadOutput = (item: MultiPackagePrintResultItem) => {
    if (source && item.printRunId) void downloadPrintRunOutput(source.organizationId, {
      id: item.printRunId,
      jobName: `${resultPackageLabel(item)} ${item.itemName ?? ''}`.trim(),
      packageLabel: item.packageLabel ?? undefined,
      packageCoaId: item.packageCoaId ?? undefined,
    });
  };

  const downloadCsv = async (item: MultiPackagePrintResultItem) => {
    if (!active || !source || !item.printRunId) return;
    const requestGeneration = requestGenerationRef.current;
    setErrorMessage(null);
    try {
      const manifest = result?.outputManifest.items.find((entry) => entry.printRunId === item.printRunId);
      const artifact = await createPrintRunCsvArtifact({
        id: item.printRunId,
        organizationId: source.organizationId,
        jobName: `${resultPackageLabel(item)} ${item.itemName ?? ''}`.trim(),
        packageLabel: item.packageLabel ?? undefined,
        packageCoaId: item.packageCoaId ?? undefined,
        expectedChildOutput: manifest ? {
          count: manifest.count, offset: manifest.offset, endExclusive: manifest.endExclusive,
        } : undefined,
      });
      if (requestGeneration === requestGenerationRef.current) downloadArtifact(artifact);
    } catch (error) {
      if (requestGeneration === requestGenerationRef.current)
        setErrorMessage(error instanceof Error ? error.message : 'The child CSV could not be created.');
    }
  };

  const downloadZip = async () => {
    if (!active || !result || !source || !result.outputManifest.items.length) return;
    const requestGeneration = requestGenerationRef.current;
    if (zipInFlightRef.current === requestGeneration || generationInFlightRef.current === requestGeneration) return;
    zipInFlightRef.current = requestGeneration;
    setIsDownloadingZip(true);
    setErrorMessage(null);
    try {
      const artifact = await createMultiPackageZip(source.organizationId, result);
      // Rendering each child can take long enough for the operator to switch
      // sources or change the selection. Never download or confirm a stale batch.
      if (requestGeneration !== requestGenerationRef.current) return;
      downloadArtifact(artifact);
      const pending = result.outputManifest.items.filter((item) => result.items.some((child) =>
        child.sequence === item.sequence && child.stage === 'OutputPending'));
      if (pending.length > 0) {
        const completed = await confirmMultiPackageOutput(source.organizationId, result.batchId,
          pending.map((item) => item.sequence));
        if (requestGeneration === requestGenerationRef.current) setResult(completed);
      }
    } catch (error) {
      if (requestGeneration === requestGenerationRef.current) {
        setErrorMessage(error instanceof Error ? error.message : 'The package ZIP could not be created.');
      }
    } finally {
      if (zipInFlightRef.current === requestGeneration) zipInFlightRef.current = null;
      if (requestGeneration === requestGenerationRef.current) setIsDownloadingZip(false);
    }
  };
  const importCandidate = async (candidate: typeof candidates[number], productId?: number) => {
    if (!active || !source || (!candidate.productId && !productId)) return;
    const sourceScope = sourceScopeRef.current;
    const importKey = `${sourceScope.version}:${candidate.metrcPackageId}`;
    if (importingIdsRef.current.has(importKey)) return;
    importingIdsRef.current.add(importKey);
    setImportingIds((current) => [...current, candidate.metrcPackageId]);
    setImportErrors((current) => ({ ...current, [candidate.metrcPackageId]: undefined }));
    try {
      const imported = await importChildPackage({
        organizationId: source.organizationId,
        sourcePackageId: source.sourcePackageId,
        sourceMetrcPackageId: source.sourceMetrcPackageId,
        facilityId: source.facilityId,
        metrcPackageId: candidate.metrcPackageId,
        productId: candidate.productId ?? productId,
      });
      if (sourceScope !== sourceScopeRef.current) return;
      updateCandidate(imported);
      refreshCandidates();
    } catch (error) {
      if (sourceScope !== sourceScopeRef.current) return;
      handleSsoError(error);
      setImportErrors((current) => ({
        ...current,
        [candidate.metrcPackageId]: getErrorMessage(error, 'Child package import failed.'),
      }));
    } finally {
      importingIdsRef.current.delete(importKey);
      if (sourceScope === sourceScopeRef.current) {
        setImportingIds((current) => current.filter((id) => id !== candidate.metrcPackageId));
      }
    }
  };

  return {
    active,
    candidates,
    selectedChildren,
    sourceCasePlan: source && Number.isSafeInteger(source.quantity) && source.quantity! >= 0
      ? commonCasePlan(source.quantity!, candidates) : null,
    results: result?.items,
    batchStatus: result?.status,
    onSelectionChange: (children: SelectedChildPackage[]) => {
      requestGenerationRef.current += 1;
      setIsDownloadingZip(false);
      operationKeyRef.current = null;
      setResult(null);
      setErrorMessage(null);
      setIsGenerating(false);
      setSelectedChildren(children);
    },
    onImport: importCandidate,
    importProductIds,
    onImportProductChange: (metrcPackageId: number, productId: number | undefined) => setImportProductIds((current) => ({
      ...current,
      [metrcPackageId]: productId,
    })),
    importingMetrcPackageIds: importingIds,
    importErrors,
    onGenerate: () => run(),
    onRetry: () => run(true),
    onResume: () => run(true),
    onDownloadOutput: downloadOutput,
    onDownloadCsv: downloadCsv,
    onDownloadZip: downloadZip,
    isLoading: isDiscovering || isGenerating,
    isDownloadingZip,
    errorMessage: errorMessage || (discoveryError ? 'Child packages could not be loaded.' : null),
    estimatedZipBytes: result?.estimatedZipBytes,
    childModeUnavailableReason,
    limits,
  };
}
